/* OEST footer game — a one-button mountain-bike runner through San Francisco (in the spirit of Chrome's offline dino game).
   Space / ↑ / click / tap to jump; hold for a higher jump. Clear the street furniture, ride kickers over parked cable
   cars, ramp up onto rooftops, Muni buses and stair streets. Pressing again in the air throws a random trick (off a
   normal jump it's also a little double-jump boost) — land it for bonus metres, land mid-trick and you bail.
   Score = metres ridden + trick bonuses. Best score is kept per device. If the section has a score endpoint
   configured, a finished run can be submitted with 3-letter initials to the public scoreboard. */
(function () {
  'use strict';
  var REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var TOUCH = window.matchMedia('(pointer: coarse)').matches;
  var ACT = TOUCH ? 'TAP' : 'SPACE / CLICK';
  var BEST_KEY = 'oest-trail-best';
  var INITIALS_KEY = 'oest-trail-initials';

  // Physics — keep in sync with the score service's plausibility check (oest-scores-worker/src/logic.js)
  var G_NORMAL = 2600, G_RAMP = 1700, JUMP_V = 560;
  var TRICK_DUR = 0.32, RAMP_BONUS = 30, AIR_BONUS = 10, RAMP_GAP = 50, BOOST_V = 380;
  var STEP_UP = 9;                // a riding bike can roll up a lip this tall (stairs, roof steps)
  var TRICKS = [
    { id: 'backflip', name: 'BACKFLIP' },
    { id: 'tailwhip', name: 'TAILWHIP' },
    { id: 'superman', name: 'SUPERMAN' },
    { id: 'nohander', name: 'NO-HANDER' }
  ];
  // Things you can ride on: ramps (sloped) and platforms (flat tops, solid sides)
  var SURFACES = { ramp: 1, house: 1, bus: 1, step: 1, landing: 1, cliff: 1 };
  // Riding-surface height at x: ramps slope up, gabled roofs rise to a ridge, everything else is flat
  function topAt(o, px) {
    if (o.type === 'ramp') return o.h * (px - o.x) / o.w;
    if (o.gable) { var half = o.w / 2; return o.h + o.gable * Math.max(0, 1 - Math.abs(px - (o.x + half)) / half); }
    return o.h;
  }
  function ease(p) { return p < 0.5 ? 2 * p * p : 1 - Math.pow(-2 * p + 2, 2) / 2; }

  function Game(root) {
    this.root = root;
    this.canvas = root.querySelector('[data-tg-canvas]');
    this.ctx = this.canvas.getContext('2d');
    this.scoreEl = root.querySelector('[data-tg-score]');
    this.bestEl = root.querySelector('[data-tg-best]');
    this.liveEl = root.querySelector('[data-tg-live]');
    this.form = root.querySelector('[data-tg-form]');
    this.endpoint = (root.dataset.endpoint || '').replace(/\/+$/, '');
    this.best = parseInt(localStorage.getItem(BEST_KEY) || '0', 10) || 0;
    this.state = 'idle';
    this.visible = true;
    this.held = false;
    this.last = 0;
    this.raf = null;
    this.runToken = null;
    this.color = getComputedStyle(root).color || '#fff';
    this.resize();
    this.reset();
    this.bind();
    this.paintBest();
    this.draw();
  }

  Game.prototype.resize = function () {
    var dpr = Math.min(window.devicePixelRatio || 1, 2);
    var w = this.canvas.clientWidth || 600, h = this.canvas.clientHeight || 180;
    this.W = w; this.H = h; this.ground = h - 26;
    this.canvas.width = Math.round(w * dpr); this.canvas.height = Math.round(h * dpr);
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    this.riderX = Math.max(40, Math.min(90, w * 0.08));
  };

  Game.prototype.reset = function () {
    this.speed = 330;            // px per second
    this.dist = 0;               // px travelled
    this.y = 0;                  // height above ground (px, positive = up)
    this.vy = 0;
    this.onGround = true;
    this.tilt = 0;
    this.crank = 0;
    this.obstacles = [];
    this.nextGap = 420;
    this.groundOffset = 0;
    this.hillOffset = 0;
    this.crashT = 0;
    this.lastMilestone = 0;
    this.flash = 0;
    this.rampAir = false;       // airborne off a ramp (floatier, bigger trick bonus)
    this.support = null;        // the surface being ridden on (null = street)
    this.trick = null;          // { def, t, done }
    this.trickUsed = false;     // one trick per air
    this.bonus = 0;             // metres from landed tricks
    this.popup = null;          // { text, t }
  };

  /* ---------- input ---------- */
  Game.prototype.bind = function () {
    var self = this;
    function press(e) {
      if (e) e.preventDefault();
      self.held = true;
      if (self.state === 'idle' || self.state === 'over') { if (self.state === 'over' && performance.now() - self.crashT < 450) return; self.start(); }
      if (self.state === 'running' && !self.onGround && !self.trickUsed && !self.inTunnel) self.startTrick();
      else self.jump();
    }
    function release() { self.held = false; }
    this.canvas.addEventListener('pointerdown', function (e) { self.canvas.focus({ preventScroll: true }); press(e); });
    window.addEventListener('pointerup', release);
    this.canvas.addEventListener('keydown', function (e) {
      if (e.code === 'Space' || e.code === 'ArrowUp' || e.code === 'KeyW' || e.key === 'Enter') { if (!e.repeat) press(e); else e.preventDefault(); }
    });
    this.canvas.addEventListener('keyup', function (e) { if (e.code === 'Space' || e.code === 'ArrowUp' || e.code === 'KeyW' || e.key === 'Enter') release(); });
    // Pause when scrolled away or tab hidden
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (entries) {
        self.visible = entries[0].isIntersecting;
        if (!self.visible && self.state === 'running') self.pause();
      }, { threshold: 0.2 }).observe(this.canvas);
    }
    document.addEventListener('visibilitychange', function () { if (document.hidden && self.state === 'running') self.pause(); });
    var rt; window.addEventListener('resize', function () { clearTimeout(rt); rt = setTimeout(function () { self.resize(); if (self.state !== 'running') self.draw(); }, 120); });
    if (this.form) this.form.addEventListener('submit', function (e) { e.preventDefault(); self.submit(); });
  };

  Game.prototype.start = function () {
    if (this.state === 'paused') { this.state = 'running'; this.loop(); return; }
    this.reset();
    this.state = 'running';
    this.bailed = null;
    this.hideForm();
    this.root.classList.add('is-running');
    this.say('Riding. Press Space or tap to jump.');
    this.requestToken();
    this.loop();
  };
  Game.prototype.pause = function () {
    this.state = 'paused';
    this.root.classList.remove('is-running');
    cancelAnimationFrame(this.raf); this.raf = null;
    this.draw();
  };
  Game.prototype.jump = function () {
    if (this.state !== 'running' || !this.onGround) return;
    if (this.support && this.support.type === 'ramp') { this.launch(); return; } // jumping on a ramp = going off it
    this.vy = JUMP_V; this.onGround = false; this.support = null; this.trickUsed = false;
  };
  // Off the lip: floaty arc, capped so the apex stays inside the canvas
  Game.prototype.launch = function () {
    var cap = Math.sqrt(2 * G_RAMP * Math.max(30, this.ground - 50 - this.y));
    this.vy = Math.min(520 + this.speed * 0.2, cap); this.onGround = false; this.support = null;
    this.rampAir = true; this.trickUsed = false; this.trick = null;
  };
  // Second press in the air: a double jump (always adds height, floatier while the trick lasts) with a random trick.
  // Tall things — palm trees, food trucks, scaffolding, the cliff — can only be cleared this way.
  Game.prototype.startTrick = function () {
    this.trickUsed = true;
    if (!this.rampAir) this.vy = Math.max(this.vy * 0.5 + 260, 420);
    this.trick = { def: TRICKS[Math.floor(Math.random() * TRICKS.length)], t: 0, done: false, ramp: this.rampAir };
  };
  Game.prototype.metres = function () { return Math.floor(this.dist / 10) + this.bonus; };

  /* ---------- loop ---------- */
  Game.prototype.loop = function () {
    var self = this;
    this.last = performance.now();
    cancelAnimationFrame(this.raf);
    function frame(now) {
      if (self.state !== 'running') return;
      var dt = Math.min(0.033, (now - self.last) / 1000); self.last = now;
      self.update(dt); self.draw();
      self.raf = requestAnimationFrame(frame);
    }
    this.raf = requestAnimationFrame(frame);
  };

  // Highest rideable surface under x whose top is at or below maxY → { h, o } (street = 0)
  Game.prototype.floorAt = function (px, maxY, rampsToo) {
    var best = { h: 0, o: null };
    for (var i = 0; i < this.obstacles.length; i++) {
      var o = this.obstacles[i];
      if (!SURFACES[o.type] || px < o.x || px > o.x + o.w) continue;
      if (o.type === 'ramp' && !rampsToo) continue;
      var top = topAt(o, px);
      if (top <= maxY + 0.01 && top > best.h) best = { h: top, o: o };
    }
    return best;
  };

  Game.prototype.update = function (dt) {
    this.speed = Math.min(900, this.speed + 7 * dt);
    var dx = this.speed * dt;
    this.dist += dx;
    this.groundOffset = (this.groundOffset + dx) % 2000;
    this.hillOffset = (this.hillOffset + dx * 0.12) % 300000;
    this.crank += dx / 9;            // wheel rotation (pedals turn at a fraction of this)
    var meters = Math.floor(this.dist / 10);
    if (meters > 0 && meters % 100 === 0 && meters !== this.lastMilestone) { this.lastMilestone = meters; this.flash = 0.6; }
    if (this.flash > 0) this.flash -= dt;

    // spawn + scroll
    this.nextGap -= dx;
    if (this.nextGap <= 0) this.spawn();
    for (var i = 0; i < this.obstacles.length; i++) this.obstacles[i].x -= dx;
    this.obstacles = this.obstacles.filter(function (o) { return o.x + o.w > -80; });

    // what's under the bike: ramps follow the bike's middle, platforms either wheel
    var rear = this.riderX + 4, mid = this.riderX + 18, front = this.riderX + 33;
    var reach = this.y + (this.onGround ? STEP_UP : 4);
    var f = this.floorAt(mid, reach, true), fr = this.floorAt(rear, reach), ff = this.floorAt(front, reach);
    if (fr.h > f.h) f = fr; if (ff.h > f.h) f = ff;

    // tunnel: low ceiling inside, the portal face above the opening is solid
    this.inTunnel = null;
    for (var tn = 0; tn < this.obstacles.length; tn++) { var to = this.obstacles[tn]; if (to.type === 'tunnel' && mid > to.x && mid < to.x + to.w) this.inTunnel = to; }

    var drop = this.y - f.h, onRampNow = this.support && this.support.type === 'ramp';
    if (this.onGround) {
      if (drop <= 0.5 || (drop <= STEP_UP && !onRampNow)) { this.y = f.h; this.vy = 0; this.support = f.o; } // rolling, stepping up, or down a slope/stairs
      else if (onRampNow && drop > 4) this.launch();                                                 // off the lip
      else { this.onGround = false; this.vy = 0; this.support = null; this.trickUsed = false; }        // rolled off an edge
    } else {
      var g = this.rampAir || (this.trick && !this.trick.done) ? G_RAMP : G_NORMAL;
      this.vy -= g * dt; this.y += this.vy * dt;
      var ceiling = this.ground - 46;                                  // keep the rider fully on screen
      if (this.inTunnel) ceiling = Math.min(ceiling, this.inTunnel.h - 37); // helmet room under the tunnel roof
      if (this.y > ceiling) { this.y = ceiling; if (this.vy > 0) this.vy = 0; }
      if (this.vy <= 0 && this.y <= f.h) {
        this.y = f.h; this.vy = 0; this.onGround = true; this.support = f.o;
        if (this.land()) return;
      }
    }
    if (this.trick && !this.trick.done) { this.trick.t += dt; if (this.trick.t >= TRICK_DUR) { this.trick.t = TRICK_DUR; this.trick.done = true; } }
    if (this.popup) { this.popup.t += dt; if (this.popup.t > 1) this.popup = null; }
    var sup = this.support;
    if (this.onGround) {
      var tr2 = this.floorAt(rear, this.y + STEP_UP, true).h, tf2 = this.floorAt(front, this.y + STEP_UP, true).h;
      this.tilt = Math.max(-0.6, Math.min(0.6, -Math.atan2(tf2 - tr2, front - rear)));
    } else this.tilt = Math.max(-0.5, Math.min(0.45, -this.vy / 1400));

    // collisions: the bike + rider box against street furniture, and against the sides of anything rideable
    var bx1 = this.riderX + 5, bx2 = this.riderX + 34, by = this.y, side = this.y + (this.onGround ? STEP_UP : 4);
    for (var k = 0; k < this.obstacles.length; k++) {
      var ob = this.obstacles[k];
      if (ob.type === 'ramp') continue;
      if (ob.type === 'tunnel') {                                      // hitting the portal above the opening
        var hitIn = bx2 > ob.x && bx1 < ob.x + 12, hitOut = bx2 > ob.x + ob.w - 12 && bx1 < ob.x + ob.w;
        if ((hitIn || hitOut) && by + 33 > ob.h) { this.crash(); return; }
        continue;
      }
      if (bx2 <= ob.x + 3 || bx1 >= ob.x + ob.w - 3) continue;
      if (SURFACES[ob.type]) { if (ob.h > side && bx1 < ob.x) { this.crash(); return; } } // only its front face can stop you
      else { var base = ob.base || 0; if (by < base + ob.h - 2 && by + 30 > base) { this.crash(); return; } } // street or rooftop furniture
    }
    if (this.scoreEl) this.scoreEl.textContent = this.fmt(this.metres());
  };

  // Touchdown: a finished trick scores, an unfinished one is a bail
  Game.prototype.land = function () {
    var tr = this.trick;
    this.rampAir = false; this.trick = null; this.trickUsed = false;
    if (!tr) return false;
    if (!tr.done && tr.t < TRICK_DUR * 0.9) { this.bailed = tr.def.name; this.crash(); return true; }
    var pts = tr.ramp ? RAMP_BONUS : AIR_BONUS;
    this.bonus += pts;
    this.popup = { text: tr.def.name + ' +' + pts, t: 0 };
    this.say(tr.def.name + ', plus ' + pts + ' metres.');
    return false;
  };

  // [type, width, height, weight] — taller and wider things join the mix the further you ride
  var EASY = [['cone', 14, 16, 3], ['hydrant', 14, 20, 2], ['cones', 32, 16, 2], ['barrier', 38, 18, 2], ['meter', 10, 26, 1.5], ['newsbox', 16, 24, 1.5], ['trashcan', 16, 22, 1.5]];
  var MID = [['bench', 40, 18, 1.3]];
  var HARD = [['muni', 10, 34, 1], ['roadclosed', 40, 30, 0.9]];
  var TALL = [['palm', 16, 66, 1], ['foodtruck', 70, 46, 0.9], ['scaffold', 40, 58, 0.9]]; // need a double jump
  var INNER = [['cone', 14, 16], ['cones', 32, 16], ['hydrant', 14, 20], ['trashcan', 16, 22], ['newsbox', 16, 24]]; // fit under a tunnel roof
  var OVER = [['cablecar', 62, 28], ['car', 48, 22], ['conerow', 46, 16]]; // parked right after a kicker
  function rnd(a, b) { return a + Math.random() * (b - a); }
  function pick(list) { return list[Math.floor(Math.random() * list.length)]; }

  // Set pieces you ride over or on top of. Each returns the pieces (x relative to 0) and the total length.
  var SETS = {
    // Ramp up onto a row of SF buildings: Edwardian flats (fire escapes), Victorians (ride over the gable),
    // brick warehouses (hop the water tower). Next roofs sit alongside or across a gap you jump.
    rooftops: function () {
      var out = [{ type: 'ramp', x: 0, w: 66, h: 40 }], x = 66, h = 40, n = 2 + Math.floor(Math.random() * 2);
      for (var i = 0; i < n; i++) {
        var kind = i === 0 ? pick(['edwardian', 'warehouse']) : pick(['edwardian', 'victorian', 'warehouse']);
        var w = Math.round(kind === 'warehouse' ? rnd(130, 160) : rnd(88, 118));
        var b = { type: 'house', kind: kind, x: x, w: w, h: h, seed: Math.random() };
        if (kind === 'victorian') b.gable = 14;
        out.push(b);
        if (kind === 'warehouse') out.push({ type: 'watertower', x: x + Math.round(w * rnd(0.45, 0.6)), w: 20, h: 26, base: h });
        if (kind === 'edwardian' && Math.random() < 0.5) out.push({ type: 'acunit', x: x + Math.round(w * 0.55), w: 18, h: 10, base: h });
        x += w;
        if (i < n - 1) {
          if (Math.random() < 0.55) { x += Math.round(rnd(40, 56)); h = Math.round(rnd(34, 50)); }  // gap between buildings: jump it
          else h = Math.max(34, Math.min(52, h + Math.round(rnd(-10, 8))));                       // next roof right alongside
        }
      }
      return { pieces: out, len: x };
    },
    // Kicker onto the roof of a Muni trolley bus
    bus: function () {
      return { pieces: [{ type: 'ramp', x: 0, w: 46, h: 16 }, { type: 'bus', x: 46 + 62, w: 132, h: 34, seed: Math.random() }], len: 46 + 62 + 132 };
    },
    // Cliff with a stair street down the far side: double jump up the rock face, ride down the steps
    cliff: function () {
      var out = [{ type: 'cliff', x: 0, w: 92, h: 64, seed: Math.random() }], x = 92;
      for (var hh = 56; hh >= 8; hh -= 8) { out.push({ type: 'step', x: x, w: 16, h: hh, down: true }); x += 16; }
      return { pieces: out, len: x };
    }
  };

  // Tunnel through a hill: a few seconds under a low roof with low obstacles inside
  Game.prototype.tunnel = function (x) {
    var sp = this.speed, L = Math.round(sp * rnd(3, 4)), t = { type: 'tunnel', x: x, w: L, h: 74, seed: Math.random() };
    this.obstacles.push(t);
    for (var pos = x + 200; pos < x + L - 170;) {
      var d = pick(INNER);
      this.obstacles.push({ type: d[0], w: d[1], h: d[2], x: pos, seed: Math.random() });
      pos += sp * 0.7 + d[1] + 40 + Math.random() * sp * 0.5;
    }
    return L;
  };

  Game.prototype.spawn = function () {
    var sp = this.speed, m = this.dist / 10, x = this.W + 20, span, r = Math.random(), extra = 0;
    if (m > 150 && r < 0.05) {
      span = this.tunnel(x) + sp * 0.4;
    } else if (m > 60 && r < 0.24) {
      var set = SETS[pick(Object.keys(SETS))]();
      for (var j = 0; j < set.pieces.length; j++) { set.pieces[j].x += x; this.obstacles.push(set.pieces[j]); }
      span = set.len + sp * 0.35;
    } else if (this.dist > 1500 && r < 0.36) {
      var ramp = { type: 'ramp', w: 46, h: 16, x: x }, ov = pick(OVER);
      this.obstacles.push(ramp, { type: ov[0], w: ov[1], h: ov[2], x: x + ramp.w + RAMP_GAP, seed: Math.random() });
      span = ramp.w + RAMP_GAP + ov[1] + sp * 0.5;
    } else {
      var pool = EASY.concat(m > 120 ? MID : [], m > 250 ? HARD : [], m > 200 ? TALL : []), total = 0, i;
      for (i = 0; i < pool.length; i++) total += pool[i][3];
      var q = Math.random() * total;
      for (i = 0; i < pool.length; i++) { q -= pool[i][3]; if (q <= 0) break; }
      var d = pool[Math.min(i, pool.length - 1)];
      this.obstacles.push({ type: d[0], w: d[1], h: d[2], x: x, seed: Math.random() });
      span = d[1];
      if (d[2] > 40) extra = sp * 0.45;                   // double jumps hang in the air longer
    }
    // a plain jump is airborne ~0.43 s; leave that much road plus a beat to react before the next thing
    this.nextGap = sp * 0.6 + span + 30 + extra + Math.random() * sp * 0.6;
  };

  Game.prototype.crash = function () {
    this.state = 'over';
    this.crashT = performance.now();
    this.trick = null;
    this.root.classList.remove('is-running');
    cancelAnimationFrame(this.raf); this.raf = null;
    var m = this.metres();
    this.lastScore = m;
    var isBest = m > this.best;
    if (isBest) { this.best = m; try { localStorage.setItem(BEST_KEY, String(m)); } catch (e) {} this.paintBest(); }
    this.draw();
    this.say((this.bailed ? 'Bailed the ' + this.bailed.toLowerCase() + '. ' : 'Crashed. ') + m + ' metres.' + (isBest ? ' New personal best.' : '') + ' Press Space or tap to ride again.');
    if (this.endpoint && m > 0) this.showForm(m);
  };

  /* ---------- drawing ---------- */
  Game.prototype.draw = function () {
    var c = this.ctx, W = this.W, H = this.H, G = this.ground, col = this.color;
    c.clearRect(0, 0, W, H);
    c.strokeStyle = col; c.fillStyle = col; c.lineCap = 'round'; c.lineJoin = 'round';

    // San Francisco skyline far away (slow parallax), fog drifting across it, then the street
    this.drawSkyline();
    this.drawFog();
    c.lineWidth = 1.5; c.beginPath(); c.moveTo(0, G + 0.5); c.lineTo(W, G + 0.5); c.stroke();
    c.globalAlpha = 0.45; c.lineWidth = 1.5; c.beginPath();
    for (var lx = -(this.groundOffset % 48); lx < W; lx += 48) { c.moveTo(lx, G + 13); c.lineTo(lx + 22, G + 13); }
    c.stroke(); c.globalAlpha = 1;

    // tunnels behind everything, then the rest (rooftop furniture lifted onto its roof)
    var j, ob;
    for (j = 0; j < this.obstacles.length; j++) if (this.obstacles[j].type === 'tunnel') this.drawTunnel(this.obstacles[j]);
    for (j = 0; j < this.obstacles.length; j++) {
      ob = this.obstacles[j];
      if (ob.type === 'tunnel') continue;
      if (ob.base) { c.save(); c.translate(0, -ob.base); this.drawObstacle(ob); c.restore(); } else this.drawObstacle(ob);
    }

    // rider
    this.drawRider();

    // overlay text
    c.font = '500 12px ' + getComputedStyle(this.root).fontFamily;
    c.textAlign = 'center';
    if (this.state === 'idle') this.caption(ACT + ' TO RIDE · ' + (TOUCH ? 'TAP' : 'PRESS') + ' TWICE TO DOUBLE JUMP');
    else if (this.state === 'paused') this.caption('PAUSED — ' + ACT + ' TO CARRY ON');
    else if (this.state === 'over') this.caption((this.bailed ? 'BAILED THE ' + this.bailed : 'CRASHED') + ' AT ' + this.fmtLong(this.lastScore) + ' M · ' + ACT + ' TO RIDE AGAIN');
    if (this.popup && this.state === 'running') {
      c.globalAlpha = Math.max(0, 1 - this.popup.t); c.textAlign = 'left';
      c.fillText(this.popup.text, this.riderX + 40, Math.max(14, G - this.y - 52 - this.popup.t * 12)); c.globalAlpha = 1;
    }
    if (this.flash > 0 && this.state === 'running') { c.globalAlpha = Math.min(1, this.flash * 2); c.textAlign = 'right'; c.fillText(this.fmtLong(this.lastMilestone) + ' M', W - 12, 22); c.globalAlpha = 1; }
  };
  Game.prototype.caption = function (txt) {
    var c = this.ctx, y = Math.max(28, this.ground * 0.36);
    if (c.measureText(txt).width <= this.W - 24 || txt.indexOf(' · ') < 0) { c.fillText(txt, this.W / 2, y); return; }
    var parts = txt.split(' · '); // too wide (phones): one phrase per line
    c.fillText(parts[0], this.W / 2, y - 8);
    c.fillText(parts.slice(1).join(' · '), this.W / 2, y + 10);
  };

  Game.prototype.drawObstacle = function (o) {
    var c = this.ctx, G = this.ground, x = o.x;
    c.lineWidth = 1.6;
    if (o.type === 'ramp') {
      c.beginPath(); c.moveTo(x, G); c.lineTo(x + o.w, G - o.h); c.lineTo(x + o.w, G); c.closePath(); c.stroke();
      c.beginPath(); for (var s = 8; s < o.w; s += 9) { c.moveTo(x + s, G - o.h * (s / o.w)); c.lineTo(x + s, G); } c.globalAlpha = 0.5; c.stroke(); c.globalAlpha = 1;
    } else if (o.type === 'cone') {
      this.cone(x);
    } else if (o.type === 'cones') {
      this.cone(x); this.cone(x + 18);
    } else if (o.type === 'hydrant') {
      c.fillRect(x + 2, G - 3, 10, 3);                  // base
      c.fillRect(x + 3, G - 14, 8, 12);                 // barrel
      c.fillRect(x, G - 11, 14, 3);                     // side outlets
      c.beginPath(); c.arc(x + 7, G - 14, 4, Math.PI, 0); c.fill(); // bonnet
      c.fillRect(x + 6, G - 20, 2, 3);                  // nut
    } else if (o.type === 'barrier') {
      c.beginPath();                                     // A-frame legs
      c.moveTo(x + 3, G); c.lineTo(x + 7, G - 12); c.lineTo(x + 11, G);
      c.moveTo(x + 27, G); c.lineTo(x + 31, G - 12); c.lineTo(x + 35, G);
      c.stroke();
      c.fillRect(x, G - 18, o.w, 6);                    // board…
      this.cut(function () { for (var k = -4; k < o.w; k += 8) { c.moveTo(x + k, G - 12); c.lineTo(x + k + 6, G - 18); c.lineTo(x + k + 9, G - 18); c.lineTo(x + k + 3, G - 12); } }); // …with diagonal stripes
    } else if (o.type === 'roadclosed') {              // tall double-board "road closed" barricade
      c.beginPath();
      c.moveTo(x + 3, G); c.lineTo(x + 7, G - 24); c.lineTo(x + 11, G);
      c.moveTo(x + 29, G); c.lineTo(x + 33, G - 24); c.lineTo(x + 37, G);
      c.stroke();
      c.fillRect(x, G - 30, o.w, 6); c.fillRect(x, G - 19, o.w, 6);
      this.cut(function () { [G - 24, G - 13].forEach(function (yb) { for (var k = -4; k < o.w; k += 8) { c.moveTo(x + k, yb); c.lineTo(x + k + 6, yb - 6); c.lineTo(x + k + 9, yb - 6); c.lineTo(x + k + 3, yb); } }); });
    } else if (o.type === 'meter') {                   // parking meter
      c.fillRect(x + 4, G - 16, 2, 16); c.fillRect(x + 1, G - 2, 8, 2);
      c.beginPath(); c.moveTo(x, G - 16); c.lineTo(x, G - 23); c.quadraticCurveTo(x + 5, G - 28, x + 10, G - 23); c.lineTo(x + 10, G - 16); c.closePath(); c.fill();
      this.cut(function () { c.rect(x + 2.5, G - 22, 5, 3); });
    } else if (o.type === 'newsbox') {                 // newspaper box on legs
      c.fillRect(x + 1, G - 4, 2, 4); c.fillRect(x + 13, G - 4, 2, 4);
      c.fillRect(x, G - 24, 16, 20);
      this.cut(function () { c.rect(x + 3, G - 21, 10, 7); c.rect(x + 3, G - 11, 10, 1.5); });
    } else if (o.type === 'trashcan') {                // SF green can: tapered body, lid, ribs
      c.beginPath(); c.moveTo(x + 1, G - 19); c.lineTo(x + 15, G - 19); c.lineTo(x + 13.5, G); c.lineTo(x + 2.5, G); c.closePath(); c.fill();
      c.fillRect(x, G - 22, 16, 2.5);
      this.cut(function () { for (var k = 0; k < 3; k++) c.rect(x + 4 + k * 3.2, G - 16, 1.2, 13); });
    } else if (o.type === 'bench') {                   // bus-stop bench
      c.fillRect(x, G - 10, o.w, 3);                    // seat
      c.fillRect(x + 2, G - 18, o.w - 4, 3);            // backrest
      c.fillRect(x + 4, G - 10, 2, 10); c.fillRect(x + o.w - 6, G - 10, 2, 10);
      c.fillRect(x + 4, G - 18, 2, 8); c.fillRect(x + o.w - 6, G - 18, 2, 8);
    } else if (o.type === 'muni') {                    // Muni stop pole with sign
      c.fillRect(x + 4, G - 34, 2, 34); c.fillRect(x + 1, G - 2, 8, 2);
      c.fillRect(x, G - 34, 10, 9);
      this.cut(function () { c.rect(x + 2, G - 32, 6, 1.5); c.rect(x + 2, G - 29, 6, 1.5); });
    } else if (o.type === 'cablecar') {                // cable car: roof, open bench sides, wheels
      c.fillRect(x, G - 28, o.w, 3);                    // roof
      c.fillRect(x + 4, G - 31, o.w - 8, 3);            // clerestory
      c.fillRect(x + 2, G - 12, o.w - 4, 6);            // body / running board
      this.cut(function () { for (var k = 0; k < 4; k++) c.rect(x + 6 + k * 14, G - 11, 9, 3); });
      c.beginPath();
      for (var pst = 2; pst <= o.w - 2; pst += 12) { c.moveTo(x + pst, G - 25); c.lineTo(x + pst, G - 12); } // window posts
      c.moveTo(x + o.w / 2, G - 31); c.lineTo(x + o.w / 2 + 6, G - 40);                                      // grip pole to the cable
      c.stroke();
      [x + 12, x + o.w - 12].forEach(function (wx) { c.beginPath(); c.arc(wx, G - 3, 3, 0, Math.PI * 2); c.fill(); });
    } else if (o.type === 'car') {                     // parked car silhouette
      c.beginPath();
      c.moveTo(x, G - 5); c.lineTo(x, G - 12); c.lineTo(x + 10, G - 13); c.lineTo(x + 16, G - 22); c.lineTo(x + 34, G - 22); c.lineTo(x + 41, G - 13);
      c.lineTo(x + 48, G - 12); c.lineTo(x + 48, G - 5); c.closePath(); c.fill();
      this.cut(function () { c.moveTo(x + 18, G - 20); c.lineTo(x + 24, G - 20); c.lineTo(x + 24, G - 14); c.lineTo(x + 14, G - 14); c.closePath(); c.moveTo(x + 26, G - 20); c.lineTo(x + 33, G - 20); c.lineTo(x + 38, G - 14); c.lineTo(x + 26, G - 14); c.closePath(); });
      [x + 11, x + 37].forEach(function (wx) { c.beginPath(); c.arc(wx, G - 4, 4, 0, Math.PI * 2); c.fill(); });
      this.cut(function () { [x + 11, x + 37].forEach(function (wx) { c.moveTo(wx + 1.5, G - 4); c.arc(wx, G - 4, 1.5, 0, Math.PI * 2); }); });
    } else if (o.type === 'conerow') {                 // row of cones
      this.cone(x); this.cone(x + 16); this.cone(x + 32);
    } else if (o.type === 'house') {
      this.drawBuilding(o);
    } else if (o.type === 'bus') {                     // Muni trolley bus: solid body, window band, poles up to the wires
      var bt = G - o.h;
      c.beginPath(); c.moveTo(x + 6, bt); c.lineTo(x + o.w, bt); c.lineTo(x + o.w, G - 5); c.lineTo(x, G - 5); c.lineTo(x, bt + 8); c.quadraticCurveTo(x, bt, x + 6, bt); c.fill();
      this.cut(function () {
        c.rect(x + 4, bt + 5, 10, 9);                                              // windscreen
        for (var k = 0; k < 6; k++) c.rect(x + 18 + k * 18, bt + 5, 14, 9);        // side windows
        c.rect(x + 22, bt + 18, 9, 11); c.rect(x + 76, bt + 18, 9, 11);            // doors
        c.rect(x + 2, bt + 1.5, 16, 2);                                            // destination sign
      });
      [x + 20, x + o.w - 22].forEach(function (wx) { c.beginPath(); c.arc(wx, G - 5, 5, 0, Math.PI * 2); c.fill(); });
      this.cut(function () { [x + 20, x + o.w - 22].forEach(function (wx) { c.moveTo(wx + 2, G - 5); c.arc(wx, G - 5, 2, 0, Math.PI * 2); }); });
      c.lineWidth = 1.4; c.beginPath();
      c.moveTo(x + o.w - 16, bt); c.lineTo(x + o.w - 54, bt - 18); c.moveTo(x + o.w - 22, bt); c.lineTo(x + o.w - 60, bt - 16);
      c.stroke();
    } else if (o.type === 'step') {                    // one stair: tread you ride over, riser, handrail post
      this.cut(function () { c.rect(x, G - o.h, o.w, o.h); });
      c.lineWidth = 1.4; c.beginPath();
      c.moveTo(x, G - o.h + 8); c.lineTo(x, G - o.h); c.lineTo(x + o.w, G - o.h);
      c.moveTo(x + o.w, G - o.h); c.lineTo(x + o.w, G);
      c.moveTo(x, G); c.lineTo(x + o.w, G);
      c.moveTo(x + 4, G - o.h); c.lineTo(x + 4, G - o.h - 14);                          // rail post
      c.moveTo(x + 4, G - o.h - 14); c.lineTo(x + o.w + 4, G - o.h - (o.down ? 6 : 22)); // handrail following the stairs
      c.stroke();
    } else if (o.type === 'landing') {                 // top of the stairs: deck, rail, a street lamp
      var lt = G - o.h;
      this.cut(function () { c.rect(x, lt, o.w, o.h); });
      c.lineWidth = 1.4; c.beginPath();
      c.rect(x, lt, o.w, o.h);
      c.moveTo(x + 4, lt); c.lineTo(x + 4, lt - 14); c.lineTo(x + o.w - 2, lt - 14); c.lineTo(x + o.w - 2, lt);
      c.moveTo(x + o.w * 0.6, lt); c.lineTo(x + o.w * 0.6, lt - 30); c.lineTo(x + o.w * 0.6 + 6, lt - 30); // lamp post
      for (var bs = x + 6; bs < x + o.w - 4; bs += 8) { c.moveTo(bs, lt + 4); c.lineTo(bs, G - 2); }    // retaining-wall texture
      c.stroke();
      c.beginPath(); c.arc(x + o.w * 0.6 + 7, lt - 28, 2.5, 0, Math.PI * 2); c.fill();
      c.fillRect(x - 1, lt - 1, o.w + 2, 2.5);                                           // deck edge you ride on
    } else if (o.type === 'palm') {                    // Dolores Park palm: curved trunk, frond crown
      c.lineWidth = 3; c.beginPath(); c.moveTo(x + 8, G); c.quadraticCurveTo(x + 4, G - 32, x + 9, G - 56); c.stroke();
      c.lineWidth = 1.6; c.beginPath();
      [[-18, 4], [-14, -6], [-4, -12], [8, -11], [17, -4], [19, 6]].forEach(function (f) {
        c.moveTo(x + 9, G - 56); c.quadraticCurveTo(x + 9 + f[0] * 0.5, G - 66 + f[1] * 0.2, x + 9 + f[0], G - 56 + f[1] + 6);
      });
      c.stroke();
      c.beginPath(); c.arc(x + 7, G - 54, 2, 0, Math.PI * 2); c.arc(x + 11, G - 53, 2, 0, Math.PI * 2); c.fill();
      for (var tk = 8; tk < 52; tk += 7) { this.cut(function () { c.rect(x + 3, G - tk, 10, 1); }); }  // trunk rings
    } else if (o.type === 'foodtruck') {               // food truck: box body, serving hatch + awning, cab, wheels
      c.beginPath(); c.moveTo(x, G - 6); c.lineTo(x, G - 46); c.lineTo(x + 52, G - 46); c.lineTo(x + 52, G - 32); c.lineTo(x + 64, G - 30); c.lineTo(x + 70, G - 20); c.lineTo(x + 70, G - 6); c.closePath(); c.fill();
      this.cut(function () { c.rect(x + 10, G - 38, 30, 13); c.moveTo(x + 55, G - 28); c.lineTo(x + 63, G - 27); c.lineTo(x + 67, G - 21); c.lineTo(x + 55, G - 21); c.closePath(); });
      c.lineWidth = 1.4; c.beginPath(); c.moveTo(x + 6, G - 39); c.lineTo(x + 44, G - 39); c.lineTo(x + 50, G - 33); c.stroke(); // awning
      for (var aw = x + 9; aw < x + 44; aw += 6) c.fillRect(aw, G - 40, 3, 3);
      [x + 14, x + 58].forEach(function (wx) { c.beginPath(); c.arc(wx, G - 5, 5, 0, Math.PI * 2); c.fill(); });
      this.cut(function () { [x + 14, x + 58].forEach(function (wx) { c.moveTo(wx + 2, G - 5); c.arc(wx, G - 5, 2, 0, Math.PI * 2); }); });
    } else if (o.type === 'scaffold') {                // construction scaffolding: poles, planks, cross braces
      c.lineWidth = 1.5; c.beginPath();
      [0, 20, 40].forEach(function (px) { c.moveTo(x + px, G); c.lineTo(x + px, G - 58); });
      [14, 29, 44, 58].forEach(function (py) { c.moveTo(x - 2, G - py); c.lineTo(x + 42, G - py); });
      c.moveTo(x, G - 14); c.lineTo(x + 20, G - 29); c.moveTo(x + 20, G - 29); c.lineTo(x + 40, G - 44);
      c.moveTo(x + 20, G - 14); c.lineTo(x + 40, G - 29); c.moveTo(x, G - 44); c.lineTo(x + 20, G - 58);
      c.stroke();
      [29, 58].forEach(function (py) { c.fillRect(x - 2, G - py - 2, 44, 3); });                  // planks
    } else if (o.type === 'watertower') {              // rooftop water tower: legs, tank, conical roof
      c.lineWidth = 1.4; c.beginPath();
      c.moveTo(x + 3, G); c.lineTo(x + 5, G - 10); c.moveTo(x + 17, G); c.lineTo(x + 15, G - 10); c.moveTo(x + 4, G - 5); c.lineTo(x + 16, G - 5);
      c.stroke();
      c.fillRect(x + 2, G - 21, 16, 11);
      c.beginPath(); c.moveTo(x + 1, G - 21); c.lineTo(x + 10, G - 27); c.lineTo(x + 19, G - 21); c.closePath(); c.fill();
      this.cut(function () { c.rect(x + 2, G - 17, 16, 1); c.rect(x + 2, G - 13.5, 16, 1); });  // hoops
    } else if (o.type === 'acunit') {                  // rooftop AC unit
      c.fillRect(x, G - 10, o.w, 8); c.fillRect(x + 2, G - 2, 2, 2); c.fillRect(x + o.w - 4, G - 2, 2, 2);
      this.cut(function () { c.moveTo(x + 6 + 3, G - 6); c.arc(x + 6, G - 6, 3, 0, Math.PI * 2); for (var k = 0; k < 3; k++) c.rect(x + 11 + k * 2.2, G - 9, 1, 6); });
    } else if (o.type === 'cliff') {                   // rock face you double jump up; railing + lamp on the top
      var ct = G - o.h;
      this.cut(function () { c.rect(x, ct, o.w, o.h); });
      c.lineWidth = 1.6; c.beginPath();
      c.moveTo(x + 2, G); c.lineTo(x + 6, G - 14); c.lineTo(x + 1, G - 26); c.lineTo(x + 7, G - 40); c.lineTo(x + 2, G - 52); c.lineTo(x + 5, ct); // jagged face
      c.lineTo(x + o.w, ct);
      c.stroke();
      c.save(); c.globalAlpha = 0.55; c.lineWidth = 1; c.beginPath();
      [[18, 12, 10], [34, 30, 12], [52, 18, 9], [22, 46, 11], [62, 42, 10], [44, 54, 8], [70, 24, 9]].forEach(function (r) { c.moveTo(x + r[0], G - r[1]); c.lineTo(x + r[0] + r[2], G - r[1] - 3); c.lineTo(x + r[0] + r[2] + 3, G - r[1] + 2); });
      c.stroke(); c.restore();
      c.fillRect(x + 3, ct - 1, o.w - 3, 2.5);                                            // the edge you land on
      c.lineWidth = 1.2; c.beginPath(); c.moveTo(x + 14, ct); c.lineTo(x + 14, ct - 12); c.lineTo(x + o.w, ct - 12);
      for (var rp = x + 26; rp < x + o.w; rp += 12) { c.moveTo(rp, ct); c.lineTo(rp, ct - 12); }
      c.moveTo(x + o.w * 0.5, ct); c.lineTo(x + o.w * 0.5, ct - 30); c.lineTo(x + o.w * 0.5 + 6, ct - 30);
      c.stroke();
      c.beginPath(); c.arc(x + o.w * 0.5 + 7, ct - 28, 2.5, 0, Math.PI * 2); c.fill();
    }
  };

  // SF buildings for the rooftop runs. The roofline is what you ride on; everything else is detail.
  Game.prototype.drawBuilding = function (o) {
    var c = this.ctx, G = this.ground, x = o.x, w = o.w, top = G - o.h, left = o.seed < 0.5, k;
    this.cut(function () { c.rect(x, top, w, o.h); if (o.gable) { c.moveTo(x, top); c.lineTo(x + w / 2, top - o.gable); c.lineTo(x + w, top); c.closePath(); } });
    c.lineWidth = 1.4;
    c.beginPath(); c.moveTo(x + 0.5, G); c.lineTo(x + 0.5, top); c.moveTo(x + w - 0.5, top); c.lineTo(x + w - 0.5, G); c.stroke();
    if (o.kind === 'victorian') {
      // gabled Queen Anne: roof edge you ride over the ridge on, fish-scale shingles, tall arched windows, stoop
      c.lineWidth = 2.5; c.beginPath(); c.moveTo(x - 2, top + 1); c.lineTo(x + w / 2, top - o.gable); c.lineTo(x + w + 2, top + 1); c.stroke();
      c.lineWidth = 1.4; c.beginPath(); c.moveTo(x, top); c.lineTo(x + w, top); c.stroke();
      c.save(); c.globalAlpha = 0.7; c.lineWidth = 1; c.beginPath();
      for (k = x + w / 2 - 12; k < x + w / 2 + 12; k += 5) { c.moveTo(k + 5, top - 3); c.arc(k + 2.5, top - 3, 2.5, 0, Math.PI); }
      c.stroke(); c.restore();
      c.beginPath(); c.arc(x + w / 2, top - o.gable * 0.45, 3, 0, Math.PI * 2); c.stroke();   // gable oculus
      var nW = Math.max(2, Math.floor((w - 16) / 18));
      c.beginPath();
      for (k = 0; k < nW; k++) {
        var wx = x + 9 + k * ((w - 18) / nW) + 2, ww = 8, wt = top + 7, wb = top + Math.max(18, o.h * 0.48);
        c.moveTo(wx, wb); c.lineTo(wx, wt + 4); c.quadraticCurveTo(wx + ww / 2, wt - 2, wx + ww, wt + 4); c.lineTo(wx + ww, wb); c.closePath();
      }
      c.moveTo(x + 3, top + Math.max(22, o.h * 0.55)); c.lineTo(x + w - 3, top + Math.max(22, o.h * 0.55));     // belt course
      var dx = left ? x + w - 20 : x + 10;
      c.rect(dx, G - 14, 9, 14);
      for (k = 0; k < 3; k++) { c.moveTo(dx - 6 + k * 2, G - 3 * k); c.lineTo(dx - 2 + k * 2, G - 3 * k); }  // stoop
      c.stroke();
    } else if (o.kind === 'warehouse') {
      // brick warehouse: parapet you ride on, brick courses, arched factory windows, loading door
      c.fillRect(x - 1, top - 1, w + 2, 3);
      c.save(); c.globalAlpha = 0.35; c.lineWidth = 1; c.setLineDash([6, 4]); c.beginPath();
      for (k = top + 6; k < G - 2; k += 5) { c.moveTo(x + 2, k); c.lineTo(x + w - 2, k); }
      c.stroke(); c.restore();
      this.cut(function () {
        var n = Math.floor((w - 20) / 22);
        for (var i = 0; i < n; i++) { var wx = x + 12 + i * 22, wt = top + 8; c.moveTo(wx, wt + 16); c.lineTo(wx, wt + 4); c.quadraticCurveTo(wx + 6, wt - 1, wx + 12, wt + 4); c.lineTo(wx + 12, wt + 16); c.closePath(); }
        c.rect(left ? x + w - 34 : x + 12, G - 18, 22, 18);
      });
      c.beginPath();
      var n2 = Math.floor((w - 20) / 22);
      for (k = 0; k < n2; k++) { var wx2 = x + 12 + k * 22, wt2 = top + 8; c.moveTo(wx2, wt2 + 16); c.lineTo(wx2, wt2 + 4); c.quadraticCurveTo(wx2 + 6, wt2 - 1, wx2 + 12, wt2 + 4); c.lineTo(wx2 + 12, wt2 + 16); c.closePath(); c.moveTo(wx2 + 6, wt2 + 1); c.lineTo(wx2 + 6, wt2 + 16); }
      var lx = left ? x + w - 34 : x + 12; c.rect(lx, G - 18, 22, 18);
      for (k = 1; k < 4; k++) { c.moveTo(lx, G - 18 + k * 4.5); c.lineTo(lx + 22, G - 18 + k * 4.5); }      // roll-up door
      c.stroke();
    } else {
      // Edwardian flat: cornice + dentils you ride on, bay window, fire escape zigzag, door, sometimes a garage
      c.fillRect(x - 2, top - 1, w + 4, 3);
      for (var dn = x + 3; dn < x + w - 3; dn += 6) c.fillRect(dn, top + 3, 2, 2);
      var bw = Math.min(30, Math.round(w * 0.3)), bx = left ? x + 8 : x + w - 8 - bw, bh = Math.round(o.h * 0.55);
      c.beginPath(); c.rect(bx, top + 9, bw, bh);
      c.moveTo(bx + bw / 3, top + 9); c.lineTo(bx + bw / 3, top + 9 + bh); c.moveTo(bx + 2 * bw / 3, top + 9); c.lineTo(bx + 2 * bw / 3, top + 9 + bh);
      c.moveTo(bx - 2, top + 8); c.lineTo(bx + bw + 2, top + 8);
      var fx = left ? x + w - 34 : x + 10, fy1 = top + 10, fy2 = top + Math.round(o.h * 0.5);     // fire escape
      c.rect(fx, fy1, 14, 9); c.rect(fx, fy2, 14, 9);
      c.moveTo(fx - 2, fy1 + 9); c.lineTo(fx + 16, fy1 + 9); c.moveTo(fx - 2, fy2 + 9); c.lineTo(fx + 16, fy2 + 9);
      c.moveTo(fx + 15, fy1 + 9); c.lineTo(fx + 3, fy2 + 9);                                          // ladder between balconies
      for (k = 0; k < 4; k++) { var t2 = (k + 1) / 5; c.moveTo(fx + 15 - 12 * t2 - 2, fy1 + 9 + (fy2 - fy1) * t2); c.lineTo(fx + 15 - 12 * t2 + 2, fy1 + 9 + (fy2 - fy1) * t2); }
      var dxe = left ? x + w - 18 : x + 30;
      c.rect(dxe, G - 15, 9, 15); c.moveTo(dxe - 3, G - 0.5); c.lineTo(dxe + 13, G - 0.5);
      if (o.seed > 0.7) c.rect(left ? x + w * 0.45 : x + w * 0.45, G - 13, 18, 13);
      c.stroke();
    }
  };

  // Tunnel through a hill: hill outline above, art-deco portals, ribbed walls and ceiling lights inside
  Game.prototype.drawTunnel = function (o) {
    var c = this.ctx, G = this.ground, x = o.x, w = o.w, ct = G - o.h, k;
    var hillTop = ct - 30;
    this.cut(function () { c.rect(x - 3, hillTop - 12, w + 6, G - hillTop + 11); });  // nothing from the skyline shows through the hill
    c.lineWidth = 1.5; c.beginPath();                                                // hill rising from the tops of the portals
    c.moveTo(x - 3, ct - 26); c.quadraticCurveTo(x + 6, ct - 30, x + 20, ct - 30);
    c.bezierCurveTo(x + w * 0.3, hillTop - 8, x + w * 0.7, hillTop - 8, x + w - 20, ct - 30);
    c.quadraticCurveTo(x + w - 6, ct - 30, x + w + 3, ct - 26);
    c.stroke();
    c.save(); c.globalAlpha = 0.5; c.lineWidth = 1; c.beginPath();                    // houses dotted along the hilltop
    for (k = x + 60; k < x + w - 60; k += 90) { var hy = hillTop + 2 - Math.sin(Math.PI * (k - x) / w) * 6; c.rect(k, hy - 8, 10, 8); c.moveTo(k - 1, hy - 8); c.lineTo(k + 5, hy - 12); c.lineTo(k + 11, hy - 8); }
    c.stroke(); c.restore();
    c.lineWidth = 1.5; c.beginPath(); c.moveTo(x, ct); c.lineTo(x + w, ct); c.stroke();   // tunnel roof
    c.save(); c.globalAlpha = 0.28; c.lineWidth = 1; c.beginPath();                    // wall ribs
    for (k = x + 40; k < x + w - 20; k += 70) { c.moveTo(k, ct); c.lineTo(k, G); }
    c.stroke(); c.restore();
    for (k = x + 30; k < x + w - 20; k += 70) {                                         // ceiling lights + glow
      c.fillRect(k, ct + 1, 10, 2.5);
      c.save(); c.globalAlpha = 0.12; c.beginPath(); c.moveTo(k, ct + 3); c.lineTo(k - 8, ct + 22); c.lineTo(k + 18, ct + 22); c.lineTo(k + 10, ct + 3); c.closePath(); c.fill(); c.restore();
    }
    [x, x + w - 12].forEach(function (px) {                                            // art-deco portals above the openings
      c.fillRect(px, ct - 26, 12, 26);
      c.fillRect(px - 3, ct - 30, 18, 4); c.fillRect(px + 1, ct - 34, 10, 4); c.fillRect(px + 4, ct - 38, 4, 4);
    });
    this.cut(function () { [x, x + w - 12].forEach(function (px) { c.rect(px + 3, ct - 22, 1.5, 18); c.rect(px + 7.5, ct - 22, 1.5, 18); }); });
  };
  // Traffic cone: solid with a see-through reflective band
  Game.prototype.cone = function (x) {
    var c = this.ctx, G = this.ground;
    c.fillRect(x, G - 2, 14, 2);
    c.beginPath(); c.moveTo(x + 2, G - 2); c.lineTo(x + 6, G - 16); c.lineTo(x + 8, G - 16); c.lineTo(x + 12, G - 2); c.closePath(); c.fill();
    this.cut(function () { c.rect(x + 3, G - 10, 8, 2.5); });
  };
  // Punch a shape out of what's drawn (stripes show the footer through)
  Game.prototype.cut = function (pathFn) {
    var c = this.ctx; c.save(); c.globalCompositeOperation = 'destination-out'; c.beginPath(); pathFn(); c.fill(); c.restore();
  };

  // Skyline on a 1500px strip that repeats, scrolling at a fraction of ride speed: Marin headlands → Golden Gate Bridge →
  // Presidio, Twin Peaks, Mount Sutro with Sutro Tower, then downtown (Transamerica Pyramid, Salesforce Tower).
  // Hills come from one height function so everything standing on them meets the ground exactly.
  var SEG = 1500;
  var HILLS = [[40, 110, 30], [600, 90, 18], [760, 70, 34], [840, 60, 30], [930, 80, 26]]; // [centre, half-width, height]
  function hillH(lx) {
    var h = 0;
    for (var i = 0; i < HILLS.length; i++) {
      var d = Math.abs(lx - HILLS[i][0]);
      if (d < HILLS[i][1]) h = Math.max(h, HILLS[i][2] * (0.5 + 0.5 * Math.cos(Math.PI * d / HILLS[i][1])));
    }
    return h;
  }
  // Bridge geometry (segment-local x): deck ends where the hills reach deck height; towers at Golden Gate proportions
  var DECK_H = 14, TOWER_H = 62;
  var BR = (function () {
    var xL = HILLS[0][0], xR = HILLS[1][0];
    while (hillH(xL) > DECK_H) xL++;
    while (hillH(xR) > DECK_H) xR--;
    var span = xR - xL;
    return { xL: xL, xR: xR, t1: Math.round(xL + span * 0.19), t2: Math.round(xR - span * 0.19) };
  })();
  function cableY(lx, h0) {               // height of the main cable above the bay at segment-local x
    var deck = h0 - DECK_H, top = h0 - TOWER_H + 3, low = deck - 4;
    if (lx <= BR.t1) { var u = (lx - BR.xL) / (BR.t1 - BR.xL); return deck - (deck - top) * u * u; }
    if (lx >= BR.t2) { var v = (BR.xR - lx) / (BR.xR - BR.t2); return deck - (deck - top) * v * v; }
    var mid = (BR.t1 + BR.t2) / 2, half = (BR.t2 - BR.t1) / 2, w = (lx - mid) / half;
    return low - (low - top) * w * w;
  }

  Game.prototype.drawSkyline = function () {
    var c = this.ctx, W = this.W, h0 = this.ground - 18, off = this.hillOffset % SEG;
    c.save(); c.globalAlpha = 0.3; c.lineWidth = 1;
    c.beginPath(); c.moveTo(0, h0 + 0.5); c.lineTo(W, h0 + 0.5); c.stroke(); // horizon / bay
    for (var base = -off; base < W; base += SEG) this.skylineSegment(base, h0);
    c.restore();
  };
  Game.prototype.skylineSegment = function (o, h0) {
    var c = this.ctx, lx;
    // hills (one continuous ridge line wherever there's land)
    c.beginPath();
    var pen = false;
    for (lx = -80; lx <= SEG; lx += 3) {
      var hh = hillH(lx);
      if (hh > 0.3) { if (!pen) { c.moveTo(o + lx, h0); pen = true; } c.lineTo(o + lx, h0 - hh); }
      else if (pen) { c.lineTo(o + lx, h0); pen = false; }
    }
    c.stroke();

    // Golden Gate Bridge: deck (with truss line) from hill to hill, two identical towers, cables, hangers end to end
    var deck = h0 - DECK_H, top = h0 - TOWER_H;
    c.beginPath(); c.moveTo(o + BR.xL, deck); c.lineTo(o + BR.xR, deck); c.moveTo(o + BR.xL + 4, deck + 2.5); c.lineTo(o + BR.xR - 4, deck + 2.5); c.stroke();
    [BR.t1, BR.t2].forEach(function (tx) {
      c.beginPath();
      c.moveTo(o + tx - 4, h0); c.lineTo(o + tx - 3, top); c.moveTo(o + tx + 4, h0); c.lineTo(o + tx + 3, top);
      [top + 1, top + 14, top + 27, top + 40].forEach(function (yy) { c.moveTo(o + tx - 4, yy); c.lineTo(o + tx + 4, yy); });
      c.stroke();
    });
    c.beginPath();
    for (lx = BR.xL; lx <= BR.xR; lx += 2) { var cy = cableY(lx, h0); if (lx === BR.xL) c.moveTo(o + lx, cy); else c.lineTo(o + lx, cy); }
    c.stroke();
    c.save(); c.globalAlpha *= 0.6; c.beginPath();
    for (lx = BR.xL + 8; lx < BR.xR - 4; lx += 8) {
      if (Math.abs(lx - BR.t1) < 6 || Math.abs(lx - BR.t2) < 6) continue;
      c.moveTo(o + lx, cableY(lx, h0)); c.lineTo(o + lx, deck);
    }
    c.stroke(); c.restore();

    // Sutro Tower on Mount Sutro: legs meet the slope exactly, crossbars, three masts
    var sx = HILLS[4][0], sTop = h0 - HILLS[4][2] - 34;
    c.beginPath();
    [-7, 7].forEach(function (dx) { c.moveTo(o + sx + dx, h0 - hillH(sx + dx)); c.lineTo(o + sx + dx * 0.43, sTop); });
    c.moveTo(o + sx, h0 - hillH(sx)); c.lineTo(o + sx, sTop);                       // third (centre) leg
    var midY = h0 - HILLS[4][2] - 14;
    c.moveTo(o + sx - 5, midY); c.lineTo(o + sx + 5, midY);
    c.moveTo(o + sx - 9, sTop); c.lineTo(o + sx + 9, sTop);
    c.moveTo(o + sx - 8, sTop); c.lineTo(o + sx - 8, sTop - 12); c.moveTo(o + sx, sTop); c.lineTo(o + sx, sTop - 16); c.moveTo(o + sx + 8, sTop); c.lineTo(o + sx + 8, sTop - 12);
    c.stroke();

    // Downtown on flat ground: low blocks so the two landmarks stand out
    var blocks = [[1060, 22, 24], [1084, 16, 34], [1102, 18, 28], [1150, 16, 38], [1210, 16, 30], [1228, 22, 26], [1252, 14, 36], [1268, 24, 20], [1294, 18, 28]];
    c.beginPath();
    blocks.forEach(function (b) { c.rect(o + b[0], h0 - b[2], b[1], b[2]); });
    // Transamerica Pyramid: slim pyramid, spire, the two side "wings"
    var px = o + 1132;
    c.moveTo(px - 10, h0); c.lineTo(px, h0 - 66); c.lineTo(px + 10, h0);
    c.moveTo(px, h0 - 66); c.lineTo(px, h0 - 74);
    c.moveTo(px - 6, h0 - 26); c.lineTo(px - 9, h0 - 26); c.lineTo(px - 9, h0 - 34); c.lineTo(px - 5, h0 - 34);
    c.moveTo(px + 6, h0 - 26); c.lineTo(px + 9, h0 - 26); c.lineTo(px + 9, h0 - 34); c.lineTo(px + 5, h0 - 34);
    c.stroke();
    // Salesforce Tower: the tallest — tapered shaft, rounded bullet crown with lit bands
    var fx = o + 1186, fb = 11, ft = 8, shaftTop = h0 - 70, crown = h0 - 90;
    c.beginPath();
    c.moveTo(fx - fb, h0); c.lineTo(fx - ft, shaftTop);
    c.bezierCurveTo(fx - ft, shaftTop - 12, fx - 4, crown, fx, crown);
    c.bezierCurveTo(fx + 4, crown, fx + ft, shaftTop - 12, fx + ft, shaftTop);
    c.lineTo(fx + fb, h0);
    c.moveTo(fx - ft + 1, shaftTop); c.lineTo(fx + ft - 1, shaftTop);
    c.stroke();
    c.save(); c.globalAlpha *= 0.8; c.beginPath();
    [shaftTop - 4, shaftTop - 8, shaftTop - 12].forEach(function (yy, i) { var hw = ft - 1 - i * 1.4; c.moveTo(fx - hw, yy); c.lineTo(fx + hw, yy); });
    c.moveTo(fx, shaftTop + 4); c.lineTo(fx, h0 - 4);                                 // centre mullion line
    c.stroke(); c.restore();
  };
  // Karl the Fog: three short dashed wisps drifting slowly in front of the skyline
  Game.prototype.drawFog = function () {
    var c = this.ctx, W = this.W, h0 = this.ground - 18, t = this.hillOffset;
    c.save(); c.globalAlpha = 0.2; c.lineWidth = 1.2; c.setLineDash([26, 8, 10, 8]);
    [[0.55, 24, 0, 240], [0.8, 40, 520, 180], [0.4, 54, 1100, 300]].forEach(function (f) {
      var span = W + f[3] + 200, x = span - ((t * f[0] + f[2]) % span) - f[3] - 100;
      c.beginPath(); c.moveTo(x, h0 - f[1]); c.lineTo(x + f[3], h0 - f[1]); c.stroke();
    });
    c.restore();
  };

  Game.prototype.drawRider = function () {
    var c = this.ctx, G = this.ground, x = this.riderX, y = G - this.y;
    var crashed = this.state === 'over';
    // trick pose: p runs 0→1 over the trick; w is an out-and-back blend for poses
    var tr = this.trick, id = tr ? tr.def.id : '', p = tr ? Math.min(1, tr.t / TRICK_DUR) : 0, w = Math.sin(Math.PI * p);
    var spinAll = id === 'backflip' ? -2 * Math.PI * ease(p) : 0;
    var bikeSX = id === 'tailwhip' ? Math.cos(2 * Math.PI * ease(p)) : 1;
    c.save();
    var pv = id === 'backflip' ? 14 : 0;               // flips turn around the bike's middle; tilts pivot at the wheels
    c.translate(x + 18, y - pv);
    c.rotate((crashed ? 0.5 : this.tilt) + spinAll);
    c.translate(-18, pv);
    var wr = 8.5, rearX = 3, frontX = 33, axleY = -wr;
    var spin = REDUCED ? 0 : this.crank;
    var bbX = 15, bbY = -7, seatX = 12, seatY = -21, headX = 27, headY = -22;
    var ca = spin * 0.3, cl = 4.5;                     // pedals turn slower than the wheels
    var p1x = bbX + Math.cos(ca) * cl, p1y = bbY + Math.sin(ca) * cl, p2x = bbX - Math.cos(ca) * cl, p2y = bbY - Math.sin(ca) * cl;

    // --- bike (squashed horizontally around its middle during a tailwhip) ---
    c.save();
    c.translate(18, 0); c.scale(Math.abs(bikeSX) < 0.08 ? 0.08 * (bikeSX < 0 ? -1 : 1) : bikeSX, 1); c.translate(-18, 0);
    c.lineWidth = 1.8;
    [rearX, frontX].forEach(function (wx) {
      c.beginPath(); c.arc(wx, axleY, wr, 0, Math.PI * 2); c.stroke();
      c.save(); c.globalAlpha = 0.6; c.lineWidth = 1; c.beginPath();
      for (var k = 0; k < 3; k++) { var a = spin + k * Math.PI / 3; c.moveTo(wx + Math.cos(a) * wr, axleY + Math.sin(a) * wr); c.lineTo(wx - Math.cos(a) * wr, axleY - Math.sin(a) * wr); }
      c.stroke(); c.restore();
    });
    c.lineWidth = 2;
    c.beginPath(); c.moveTo(rearX, axleY); c.lineTo(bbX, bbY); c.lineTo(headX, headY - 1); c.lineTo(seatX, seatY); c.lineTo(rearX, axleY); c.moveTo(bbX, bbY); c.lineTo(seatX, seatY); c.stroke();
    c.beginPath(); c.moveTo(headX, headY - 1); c.lineTo(frontX, axleY); c.moveTo(headX - 1, headY - 4); c.lineTo(headX + 3, headY - 6); c.stroke();
    c.lineWidth = 1.4; c.beginPath(); c.moveTo(p1x, p1y); c.lineTo(p2x, p2y); c.stroke();
    c.restore();

    // --- rider ---
    var airborne = !this.onGround && !crashed;
    var hipX = seatX + 1, hipY = seatY - 3, shX = headX - 5, shY = headY - 14;
    if (airborne) { hipY -= 3; hipX += 2; }            // standing on the pedals in the air
    var grip = [headX + 2, headY - 5];
    var feet = [[p1x, p1y], [p2x, p2y]];
    function mix(a, b, k) { return [a[0] + (b[0] - a[0]) * k, a[1] + (b[1] - a[1]) * k]; }
    if (id === 'tailwhip') { hipY -= 6 * w; feet = feet.map(function (f) { return mix(f, [f[0], f[1] - 12], w); }); }  // feet lifted while the bike spins
    if (id === 'superman') {                            // body stretched out flat behind the bars
      hipX -= 14 * w; hipY -= 2 * w; shX -= 3 * w; shY += 6 * w;
      feet = [mix(feet[0], [hipX - 16, hipY - 1], w), mix(feet[1], [hipX - 15, hipY + 3], w)];
    }
    var hand = id === 'nohander' ? mix(grip, [shX + 2, shY - 13], w) : grip;
    var hand2 = id === 'nohander' ? mix(grip, [shX - 6, shY - 11], w) : null;

    c.lineWidth = 2.6;
    c.beginPath(); c.moveTo(hipX, hipY); c.lineTo(shX, shY); c.stroke();          // torso
    c.lineWidth = 2;
    c.beginPath(); c.moveTo(shX, shY); c.lineTo(hand[0], hand[1]); if (hand2) { c.moveTo(shX, shY); c.lineTo(hand2[0], hand2[1]); } c.stroke(); // arm(s)
    c.beginPath();                                      // legs, knee bent towards the front
    feet.forEach(function (pd, idx) {
      var straight = id === 'superman';
      var kx = (hipX + pd[0]) / 2 + (straight ? 5 * (1 - w) : 5), ky = (hipY + pd[1]) / 2 - (straight ? 2 * (1 - w) : 2) + (idx ? 1 : 0);
      c.moveTo(hipX, hipY); c.lineTo(kx, ky); c.lineTo(pd[0], pd[1]);
    });
    c.stroke();
    c.beginPath(); c.arc(shX + 4, shY - 5, 3.8, 0, Math.PI * 2); c.fill();      // head
    c.lineWidth = 1.5; c.beginPath(); c.moveTo(shX + 6, shY - 8); c.lineTo(shX + 10, shY - 7); c.stroke(); // helmet peak
    c.restore();
  };

  /* ---------- scores ---------- */
  Game.prototype.fmt = function (m) { return String(m).padStart(5, '0'); };
  Game.prototype.fmtLong = function (m) { return (m || 0).toLocaleString('en-US'); };
  Game.prototype.paintBest = function () { if (this.bestEl) this.bestEl.textContent = this.fmt(this.best); };
  Game.prototype.say = function (t) { if (this.liveEl) this.liveEl.textContent = t; };

  Game.prototype.requestToken = function () {
    this.runToken = null;
    this.submitted = false;
    if (!this.endpoint) return;
    var self = this;
    fetch(this.endpoint + '/start', { method: 'POST' }).then(function (r) { return r.ok ? r.json() : null; })
      .then(function (j) { if (j && j.token) self.runToken = j.token; }).catch(function () {});
  };
  Game.prototype.showForm = function (m) {
    if (!this.form) return;
    this.form.hidden = false;
    this.form.querySelector('[data-tg-form-score]').textContent = this.fmtLong(m) + ' m';
    var input = this.form.querySelector('input[name="initials"]');
    input.value = localStorage.getItem(INITIALS_KEY) || '';
    this.setStatus('');
    this.form.querySelector('button').disabled = false;
  };
  Game.prototype.hideForm = function () { if (this.form) this.form.hidden = true; };
  Game.prototype.setStatus = function (t) { var s = this.form && this.form.querySelector('[data-tg-status]'); if (s) s.textContent = t; };
  Game.prototype.submit = function () {
    var self = this, input = this.form.querySelector('input[name="initials"]');
    var initials = (input.value || '').toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 3);
    if (initials.length !== 3) { this.setStatus('Use 3 letters or numbers.'); input.focus(); return; }
    if (this.submitted) { this.setStatus('Already submitted.'); return; }
    if (!this.runToken) { this.setStatus('Couldn’t reach the scoreboard for this run.'); return; }
    try { localStorage.setItem(INITIALS_KEY, initials); } catch (e) {}
    var btn = this.form.querySelector('button'); btn.disabled = true; this.setStatus('Submitting…');
    fetch(this.endpoint + '/submit', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ token: this.runToken, initials: initials, score: this.lastScore }) })
      .then(function (r) { return r.json().catch(function () { return {}; }).then(function (j) { return { ok: r.ok, j: j }; }); })
      .then(function (res) {
        if (res.ok) { self.runToken = null; self.submitted = true; self.setStatus(res.j.rank ? 'Submitted — you’re #' + res.j.rank + '.' : 'Submitted. It’ll appear on the scoreboard shortly.'); }
        else { btn.disabled = false; self.setStatus(res.j.error || 'That didn’t go through. Try again.'); }
      })
      .catch(function () { btn.disabled = false; self.setStatus('That didn’t go through. Try again.'); });
  };

  function init() { document.querySelectorAll('[data-trail-game]').forEach(function (el) { if (!el._tg) el._tg = new Game(el); }); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init); else init();
  document.addEventListener('shopify:section:load', init);
})();
